import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EventCard from '../components/EventCard'
import AddEventCard from '../components/AddEventCard'
import PhotoSelectorModal from '../components/PhotoSelectorModal'
import Pagination from '../components/UI/Pagination'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { usePassportEvents } from '../hooks/usePassportEvents'
import { useTheme } from '../hooks/useTheme'
import { getEventImage, getAllEventPhotos } from '../utils/eventImageUtils'

const Passport = () => {
    const navigate = useNavigate()
    const { userId } = useParams()
    const [currentPage, setCurrentPage] = useState(1)
    const [eventsPerPage] = useState(9)
    const [showPhotoSelector, setShowPhotoSelector] = useState(null)

    // `updateDefaultPhoto` — the page previously destructured
    // `updateEventDefaultPhoto`, which the hook does not export, so choosing a
    // card photo threw a TypeError. It also had nothing to save to until
    // `defaultPhoto` was added to the entry schema and update allowlist.
    const { events, loading, error, viewingFriend, isOwnPassport, totalPages, deleteEvent, updateDefaultPhoto } = usePassportEvents(userId, currentPage, eventsPerPage)
    const { isClearTheme } = useTheme()

    const handleEventClick = (eventId) => {
        navigate(`/event/${eventId}`)
    }

    const handleAddEvent = () => {
        navigate('/newEvent')
    }

    const handleDeleteEvent = async (eventId, eventName) => {
        if (window.confirm(`Are you sure you want to delete "${eventName}"?`)) {
            try {
                await deleteEvent(eventId)
            } catch (err) {
                // Error is handled in the hook
            }
        }
    }

    const handleSetDefaultPhoto = async (eventId, photoId) => {
        try {
            await updateDefaultPhoto(eventId, photoId)
            setShowPhotoSelector(null)
        } catch (err) {
            // Error is handled in the hook
        }
    }

    // The server already returns exactly this page.
    const currentEvents = events

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber)
    }

    const selectedEvent = events.find(e => e._id === showPhotoSelector)
    const selectedEventPhotos = selectedEvent ? getAllEventPhotos(selectedEvent) : []

    return (
        <div className="p-8">
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-4xl font-bold mb-2" style={{color: 'var(--theme-text-primary)'}}>
                            {isOwnPassport ? 'Event Passport' : `${viewingFriend?.profileSettings?.raveName || viewingFriend?.username || 'Friend'}'s Passport`}
                        </h1>
                        <p style={{color: 'var(--theme-text-secondary)'}}>
                            {isOwnPassport ? 'Your personal collection of events and memories' : 'Public and friends-only events'}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {!isOwnPassport && (
                            <button
                                onClick={() => navigate('/passport')}
                                className="text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 bg-gray-600 hover:bg-gray-700"
                            >
                                👤 My Passport
                            </button>
                        )}
                        {isOwnPassport && (
                            <button
                                onClick={() => navigate('/add-stamp')}
                                className="text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
                                style={{
                                    background: `linear-gradient(to right, var(--theme-primary), var(--theme-secondary))`
                                }}
                            >
                                📸 Stamp My Passport
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Privacy Legend */}
            {!loading && events.length > 0 && (
                <div className="mb-6 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4">
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                        <span className="text-sm font-medium" style={{color: 'var(--theme-text-secondary)'}}>
                            Privacy Legend:
                        </span>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg border-2 border-green-500/60 bg-white/5"></div>
                            <span className="text-sm" style={{color: 'var(--theme-text-primary)'}}>🌍 Public</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg border-2 border-blue-500/60 bg-white/5"></div>
                            <span className="text-sm" style={{color: 'var(--theme-text-primary)'}}>👥 Friends Only</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg border-2 border-purple-500/60 bg-white/5"></div>
                            <span className="text-sm" style={{color: 'var(--theme-text-primary)'}}>🔒 Private</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="text-center py-12">
                    <LoadingSpinner text="Loading events..." />
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-xl text-sm mb-4">
                    {error}
                </div>
            )}

            {/* Events Grid */}
            {!loading && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
                    {currentEvents.map((event) => (
                        <EventCard
                            key={event._id}
                            event={event}
                            isOwnPassport={isOwnPassport}
                            onEventClick={handleEventClick}
                            onDeleteEvent={handleDeleteEvent}
                            onOpenPhotoSelector={setShowPhotoSelector}
                            getEventImage={getEventImage}
                            getAllEventPhotos={getAllEventPhotos}
                        />
                    ))}

                    {/* Add Event Card - Only for own passport */}
                    {isOwnPassport && (
                        <AddEventCard onAddEvent={handleAddEvent} />
                    )}
                </div>
            )}

            {/* Pagination */}
            {!loading && events.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    className="mt-8"
                />
            )}

            {/* Empty State */}
            {!loading && events.length === 0 && !error && (
                <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 012 0v4m0 0V3a1 1 0 012 0v4m0 0h4m-4 0h4m-4 0v13a1 1 0 01-1 1H7a1 1 0 01-1-1V7m2 0h4m-4 0h4" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-300 mb-2">
                        {isOwnPassport ? 'No events in your passport' : 'No visible events'}
                    </h3>
                    <p className="text-gray-400 mb-4">
                        {isOwnPassport
                            ? 'Start creating events to build your collection!'
                            : 'This friend hasn\'t shared any public or friends-only events yet.'
                        }
                    </p>
                    {isOwnPassport && (
                        <button
                            onClick={handleAddEvent}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
                        >
                            Create First Event
                        </button>
                    )}
                </div>
            )}

            {/* Photo Selector Modal */}
            <PhotoSelectorModal
                isOpen={!!showPhotoSelector}
                event={selectedEvent}
                photos={selectedEventPhotos}
                isClearTheme={isClearTheme}
                onClose={() => setShowPhotoSelector(null)}
                onSetDefaultPhoto={handleSetDefaultPhoto}
            />
        </div>
    )
}

export default Passport